import classNames from "classnames"
import type Swiper from "swiper"
import { FreeMode } from "swiper/modules"
import { Swiper as SwiperComponent, SwiperSlide } from "swiper/react"

import CatalogueHeroItem from "./Item"
import { Color } from "../../../../../constants/V2/color"
import {
  getBackgroundColorClass,
  getAccentColor,
} from "../../../../../utils/V2/color"
import CarouselNavButton from "../../../../elements/V2/Buttons/NavButton"
import Typography from "../../../../elements/V2/Typography"

import React, { useState } from "react"

type Product = {
  name: string
  thumbnail: Storyblok.Asset
  link: string | Storyblok.Link | YocoCom.YocoLink
}

type CatalogueHeroProps = {
  headingText?: string
  products: Product[]
  onlineProducts?: Product[]
  backgroundColor?: Color
}

const CatalogueHero = ({
  headingText,
  products,
  onlineProducts = [],
  backgroundColor = Color.Charcoal,
}: CatalogueHeroProps) => {
  const [swiperInstance, setSwiperInstance] = useState<Swiper | null>(null)
  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const bgColorClass = getBackgroundColorClass(backgroundColor)
  const textColor = getAccentColor(backgroundColor)
  const handlePrev = () => {
    swiperInstance?.slidePrev()
  }

  const handleNext = () => {
    swiperInstance?.slideNext()
  }

  const handleSlideChange = (swiper: Swiper) => {
    setIsBeginning(swiper.isBeginning)
    setIsEnd(swiper.isEnd)
  }

  const mergedProducts = [...products, ...onlineProducts]
  return (
    <div
      className={classNames(
        "-mb-lg pb-lg relative z-0",
        "-mt-header pt-header",
        bgColorClass
      )}
    >
      {headingText && (
        <div className="md:pt-3xl md:mb-5xl mt-[60px] px-4 pt-8 md:mt-0">
          <Typography
            text={headingText}
            size="lead-md"
            font="grotesk"
            weight="book"
            color={textColor}
            className="px-lg text-center leading-tight md:leading-normal"
            element="h1"
          />
        </div>
      )}
      <div
        className={classNames(
          "pb-lg relative",
          headingText ? "pt-xl" : "pt-[100px]"
        )}
      >
        <CarouselNavButton
          onClick={handlePrev}
          direction="left"
          className={classNames(
            "absolute left-0 top-[55%] z-20 ml-[3%] -translate-y-1/2 transition-opacity duration-300 lg:hidden",
            isBeginning && "opacity-0"
          )}
          color={textColor}
        />
        <CarouselNavButton
          onClick={handleNext}
          direction="right"
          className={classNames(
            "absolute right-0 top-[55%] z-20 mr-[3%] -translate-y-1/2 transition-opacity duration-300 lg:hidden",
            isEnd && "opacity-0"
          )}
          color={textColor}
        />

        <SwiperComponent
          modules={[FreeMode]}
          slidesPerView="auto"
          freeMode
          grabCursor
          slidesOffsetBefore={20}
          watchOverflow
          onSlideChange={handleSlideChange}
          onSwiper={setSwiperInstance}
          className="overflow-x-hidden pt-1 lg:hidden"
        >
          {mergedProducts.map((product, index) => (
            <SwiperSlide
              key={`swiper-slide-${product.name}-${index}`}
              className="mr-20 h-auto w-min"
            >
              <CatalogueHeroItem
                name={product.name}
                thumbnail={product.thumbnail}
                link={product.link}
                color={textColor}
                backgroundColor={backgroundColor}
              />
            </SwiperSlide>
          ))}
        </SwiperComponent>

        <div className="hidden items-stretch justify-center gap-20 lg:flex">
          {mergedProducts.map((product, index) => (
            <CatalogueHeroItem
              key={`catalogue-hero-item-${product.name}-${index}`}
              name={product.name}
              thumbnail={product.thumbnail}
              link={product.link}
              color={textColor}
              backgroundColor={backgroundColor}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

export default CatalogueHero
